import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'seller_token';
const LOCAL_DRAFT_KEY = (branch) => `inv_draft_${branch}_v1`;

const iax = axios.create({ baseURL: API });
iax.interceptors.request.use((cfg) => {
  const tok = localStorage.getItem(TOKEN_KEY);
  if (tok) cfg.headers.Authorization = `Bearer ${tok}`;
  return cfg;
});

function statusFor(expected, counted) {
  if (counted === null || counted === undefined || counted === '') return null;
  const c = Number(counted);
  const e = Number(expected || 0);
  const diff = c - e;
  if (diff === 0) return { label: 'Correcto', tone: 'ok', diff };
  if (diff > 0) return { label: `Sobran ${diff}`, tone: 'over', diff };
  return { label: `Faltan ${Math.abs(diff)}`, tone: 'under', diff };
}

const toneStyles = {
  ok: 'bg-emerald-50 border-emerald-300 text-emerald-800',
  over: 'bg-amber-50 border-amber-300 text-amber-800',
  under: 'bg-rose-50 border-rose-300 text-rose-800',
};

// Mapa: tipo de movimiento (backend) → { etiqueta, kind lógico para detalle }
const MOV_TYPE_MAP = {
  salida: { label: 'Envío / Salida', kind: 'shipment', tone: 'out' },
  entrada: { label: 'Entrada manual', kind: null, tone: 'in' },
  merma: { label: 'Merma', kind: null, tone: 'out' },
  apartado_entrega: { label: 'Apartado (entrega)', kind: 'apartado', tone: 'out' },
  sale_cancelled: { label: 'Cancelación venta', kind: 'sale', tone: 'in' },
  sale_edit_apply: { label: 'Edición venta (aplicar)', kind: 'sale', tone: 'mut' },
  sale_edit_revert: { label: 'Edición venta (reversa)', kind: 'sale', tone: 'mut' },
  purchase_cancelled: { label: 'Cancelación compra', kind: 'purchase', tone: 'out' },
  transfer_in: { label: 'Transferencia entrada', kind: 'transfer', tone: 'in' },
  transfer_out: { label: 'Transferencia salida', kind: 'transfer', tone: 'out' },
  transfer_cancelled: { label: 'Cancelación transf.', kind: 'transfer', tone: 'mut' },
  shipment_edited: { label: 'Edición envío', kind: 'shipment', tone: 'mut' },
  shipment_edit_apply: { label: 'Edición envío (aplicar)', kind: 'shipment', tone: 'mut' },
  shipment_edit_revert: { label: 'Edición envío (reversa)', kind: 'shipment', tone: 'mut' },
  physical_adjustment: { label: 'Ajuste inventario', kind: null, tone: 'mut' },
};

function labelForMov(m) {
  return MOV_TYPE_MAP[m.type]?.label || m.type;
}

export default function PhysicalInventoryAdmin({ onExit }) {
  const [branch, setBranch] = useState(() => localStorage.getItem('inv_branch_tab') || 'morelos');
  const [products, setProducts] = useState([]);
  const [branchName, setBranchName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | pending | mismatch | ok
  const [savingIds, setSavingIds] = useState(new Set());
  const [infoModal, setInfoModal] = useState(null); // {product, movements, sales}
  const [imgModal, setImgModal] = useState(null); // { url, name }
  const [summary, setSummary] = useState(null);
  const [pending, setPending] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);

  // Save session (Ajustes rápidos)
  const [applyAdjustments, setApplyAdjustments] = useState(true);
  const [saveReason, setSaveReason] = useState('');
  const [saveSignature, setSaveSignature] = useState(() => localStorage.getItem('inv_signature') || '');
  const [saving, setSaving] = useState(false);
  const [savedResult, setSavedResult] = useState(null);
  const saveTimers = useRef({});

  useEffect(() => { localStorage.setItem('inv_branch_tab', branch); }, [branch]);

  const load = async () => {
    setLoading(true); setError(''); setSummary(null); setPending(null);
    try {
      const { data } = await iax.get(`/public/inventory/products?branch=${branch}`);
      setBranchName(data.branch_name);
      // Merge in local drafts (values typed but not yet saved to server, e.g. during transient offline)
      let draft = {};
      try { draft = JSON.parse(localStorage.getItem(LOCAL_DRAFT_KEY(branch)) || '{}'); } catch { /* ignore */ }
      const merged = (data.products || []).map(p => ({
        ...p,
        counted_qty: p.counted_qty ?? (draft[p.product_id] ?? null),
      }));
      setProducts(merged);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error al cargar inventario');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branch]);

  // Contadores globales
  const stats = useMemo(() => {
    let counted = 0, pending = 0, ok = 0, mismatch = 0;
    for (const p of products) {
      if (p.counted_qty === null || p.counted_qty === undefined || p.counted_qty === '') { pending++; continue; }
      counted++;
      const s = statusFor(p.system_stock, p.counted_qty);
      if (!s) continue;
      if (s.tone === 'ok') ok++;
      else mismatch++;
    }
    return { total: products.length, counted, pending, ok, mismatch };
  }, [products]);

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    return products.filter(p => {
      if (filter === 'pending' && !(p.counted_qty === null || p.counted_qty === undefined || p.counted_qty === '')) return false;
      if (filter === 'ok') {
        const s = statusFor(p.system_stock, p.counted_qty);
        if (!s || s.tone !== 'ok') return false;
      }
      if (filter === 'mismatch') {
        const s = statusFor(p.system_stock, p.counted_qty);
        if (!s || s.tone === 'ok') return false;
      }
      if (t) {
        return (p.name || '').toLowerCase().includes(t) || (p.sku || '').toLowerCase().includes(t);
      }
      return true;
    });
  }, [products, search, filter]);

  const onCountChange = (productId, value) => {
    setProducts(prev => prev.map(p => p.product_id === productId ? { ...p, counted_qty: value === '' ? '' : Math.max(0, parseInt(value) || 0) } : p));
    // Update local draft immediately (survives reload while pending server save)
    try {
      const raw = localStorage.getItem(LOCAL_DRAFT_KEY(branch)) || '{}';
      const draft = JSON.parse(raw);
      if (value === '' || value === null) delete draft[productId];
      else draft[productId] = Math.max(0, parseInt(value) || 0);
      localStorage.setItem(LOCAL_DRAFT_KEY(branch), JSON.stringify(draft));
    } catch { /* ignore quota */ }

    // Debounce server save (400ms)
    clearTimeout(saveTimers.current[productId]);
    if (value === '' || value === null) return; // don't save empty
    const qty = Math.max(0, parseInt(value) || 0);
    saveTimers.current[productId] = setTimeout(async () => {
      setSavingIds(prev => new Set([...prev, productId]));
      try {
        await iax.post('/public/inventory/count', {
          branch, product_id: productId, counted_qty: qty,
        });
      } catch (e) {
        setError(e?.response?.data?.detail || 'No se pudo guardar el conteo');
      } finally {
        setSavingIds(prev => { const n = new Set(prev); n.delete(productId); return n; });
      }
    }, 400);
  };

  const refreshStock = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await iax.get(`/public/inventory/products?branch=${branch}`);
      // Preserve unsaved typing (if any) by only updating stock/en_ruta
      setProducts(prev => {
        const map = Object.fromEntries((data.products || []).map(p => [p.product_id, p]));
        return prev.map(p => {
          const fresh = map[p.product_id];
          if (!fresh) return p;
          return { ...fresh, counted_qty: p.counted_qty ?? fresh.counted_qty };
        });
      });
    } catch (e) {
      setError('No se pudo refrescar el stock');
    } finally { setLoading(false); }
  };

  const resetBranch = async () => {
    setConfirmReset(false);
    try {
      await iax.post(`/public/inventory/reset?branch=${branch}`);
      try { localStorage.removeItem(LOCAL_DRAFT_KEY(branch)); } catch {}
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo reiniciar');
    }
  };

  const openInfo = async (product) => {
    setInfoModal({ product, movements: null, sales: null, loading: true });
    try {
      const { data } = await iax.get(`/public/inventory/product-info?product_id=${product.product_id}&branch=${branch}&limit=30`);
      setInfoModal({ product, movements: data.recent_movements || [], sales: data.recent_sales || [], loading: false });
    } catch (e) {
      setInfoModal({ product, movements: [], sales: [], loading: false, error: 'Error al cargar' });
    }
  };

  // Cargar detalle de una operación (venta/envío/etc.) al hacer doble-clic
  const [refDetail, setRefDetail] = useState(null); // { loading, kind, data, error }
  const openRefDetail = async (refId) => {
    if (!refId) return;
    setRefDetail({ loading: true, refId });
    try {
      const { data } = await iax.get(`/public/inventory/movement-detail?ref_id=${refId}`);
      setRefDetail({ loading: false, ...data });
    } catch (e) {
      setRefDetail({ loading: false, error: e?.response?.data?.detail || 'No se encontró detalle' });
    }
  };

  const finalize = async () => {
    setError(''); setSummary(null); setPending(null);
    try {
      const { data } = await iax.post(`/public/inventory/finalize?branch=${branch}`);
      if (data.ok) {
        setSummary(data);
      } else {
        setPending(data);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error al finalizar');
    }
  };

  const downloadPdf = () => {
    const tok = localStorage.getItem(TOKEN_KEY);
    const url = `${API}/public/inventory/pdf?branch=${branch}&token=${encodeURIComponent(tok || '')}`;
    window.open(url, '_blank');
  };

  const saveSession = async () => {
    setError('');
    if (applyAdjustments && saveReason.trim().length < 5) {
      setError('Escribe el motivo del ajuste (mín. 5 caracteres)');
      return;
    }
    setSaving(true);
    try {
      const { data } = await iax.post(`/public/inventory/save-session`, {
        branch,
        apply_adjustments: applyAdjustments,
        reason: saveReason.trim() || null,
        signature: saveSignature.trim() || null,
      });
      if (saveSignature.trim()) {
        try { localStorage.setItem('inv_signature', saveSignature.trim()); } catch {}
      }
      try { localStorage.removeItem(LOCAL_DRAFT_KEY(branch)); } catch {}
      setSavedResult(data);
      setSummary(null);
      setSaveReason('');
      // Reload fresh
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo guardar el inventario');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-4" data-testid="physical-inventory">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">📋 Inventario físico</h2>
          <p className="text-xs text-slate-500">Sucursal <b>{branchName || '…'}</b> · Cambios se guardan solos</p>
        </div>
        <div className="flex gap-2">
          <button onClick={onExit} className="text-xs bg-slate-200 hover:bg-slate-300 px-3 py-1.5 rounded-full" data-testid="inv-back-btn">← Volver</button>
        </div>
      </div>

      {/* Tabs sucursal */}
      <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1 text-sm w-fit">
        {[
          { id: 'morelos', label: '🟢 Morelos' },
          { id: 'america', label: '🔵 América' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setBranch(t.id)}
            className={`px-4 py-1.5 rounded-md font-semibold ${branch === t.id ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}
            data-testid={`inv-branch-${t.id}`}
          >{t.label}</button>
        ))}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <div className="bg-white border-2 border-slate-200 rounded-xl p-3 text-center">
          <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Total</p>
          <p className="text-2xl font-black text-slate-900">{stats.total}</p>
        </div>
        <div className="bg-white border-2 border-slate-200 rounded-xl p-3 text-center">
          <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Contados</p>
          <p className="text-2xl font-black text-slate-900" data-testid="inv-kpi-counted">{stats.counted}</p>
        </div>
        <div className="bg-white border-2 border-rose-200 rounded-xl p-3 text-center">
          <p className="text-[10px] uppercase tracking-widest text-rose-500 font-semibold">Pendientes</p>
          <p className="text-2xl font-black text-rose-700" data-testid="inv-kpi-pending">{stats.pending}</p>
        </div>
        <div className="bg-white border-2 border-emerald-200 rounded-xl p-3 text-center">
          <p className="text-[10px] uppercase tracking-widest text-emerald-500 font-semibold">Correctos</p>
          <p className="text-2xl font-black text-emerald-700">{stats.ok}</p>
        </div>
        <div className="bg-white border-2 border-amber-200 rounded-xl p-3 text-center">
          <p className="text-[10px] uppercase tracking-widest text-amber-500 font-semibold">Con diferencia</p>
          <p className="text-2xl font-black text-amber-700">{stats.mismatch}</p>
        </div>
      </div>

      {/* Filters + actions */}
      <div className="flex flex-wrap gap-2 items-center bg-white border border-slate-200 rounded-xl p-2">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nombre o SKU…"
          className="flex-1 min-w-[180px] px-3 py-2 border border-slate-200 rounded-lg text-sm"
          data-testid="inv-search"
        />
        <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1 text-xs">
          {[
            { id: 'all', label: 'Todos' },
            { id: 'pending', label: 'Pendientes' },
            { id: 'ok', label: 'Correctos' },
            { id: 'mismatch', label: 'Diferencia' },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setFilter(t.id)}
              className={`px-2.5 py-1 rounded-md ${filter === t.id ? 'bg-white shadow text-slate-800 font-semibold' : 'text-slate-500'}`}
              data-testid={`inv-filter-${t.id}`}
            >{t.label}</button>
          ))}
        </div>
        <button onClick={refreshStock} className="text-xs px-3 py-2 bg-slate-800 text-white rounded-lg" data-testid="inv-refresh-stock">🔄 Actualizar</button>
        <button onClick={() => setConfirmReset(true)} className="text-xs px-3 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg" data-testid="inv-reset">🗑️ Reiniciar</button>
        <button onClick={finalize} className="text-xs px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold" data-testid="inv-finalize">✅ Terminar inventario</button>
      </div>

      {error && <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-3 text-sm">{error}</div>}

      {loading && <p className="text-center text-slate-500 py-8">Cargando productos…</p>}

      {/* Product rows (linear compact layout) */}
      {!loading && (
        <div className="space-y-1.5">
          {filtered.map(p => {
            const s = statusFor(p.system_stock, p.counted_qty);
            const isSaving = savingIds.has(p.product_id);
            return (
              <div
                key={p.product_id}
                className={`bg-white border rounded-lg pl-2 pr-2 py-1.5 flex items-center gap-3 ${
                  s ? (s.tone === 'ok' ? 'border-emerald-200' : s.tone === 'over' ? 'border-amber-300' : 'border-rose-300') : 'border-slate-200'
                }`}
                data-testid={`inv-row-${p.product_id}`}
              >
                {/* Image (small, tappable) */}
                <button
                  onClick={() => p.image_url && setImgModal({ url: p.image_url, name: p.name })}
                  className="w-11 h-11 bg-slate-100 rounded-md flex-shrink-0 overflow-hidden border border-slate-200"
                  aria-label="Ver imagen"
                  data-testid={`inv-image-${p.product_id}`}
                >
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300 text-lg">📦</div>
                  )}
                </button>

                {/* Info (all in one line) */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate leading-tight">{p.name}</p>
                  <div className="flex flex-wrap gap-x-3 items-center text-[11px] text-slate-500 leading-tight">
                    <span className="font-mono">{p.sku || '-'}</span>
                    <span>Sistema: <b className="text-slate-900">{p.system_stock}</b></span>
                    {p.purchase_cost != null && (
                      <span className="text-slate-400">(${Number(p.purchase_cost).toFixed(2)})</span>
                    )}
                    {p.en_ruta_qty > 0 && (
                      <span className="text-sky-700 font-semibold">🚚 {p.en_ruta_qty} en ruta</span>
                    )}
                  </div>
                </div>

                {/* Balance chip */}
                {s ? (
                  <span className={`hidden sm:inline text-[10px] px-2 py-0.5 rounded-full border font-bold whitespace-nowrap ${toneStyles[s.tone]}`}
                        data-testid={`inv-balance-${p.product_id}`}>
                    {s.label}
                  </span>
                ) : (
                  <span className="hidden sm:inline text-[10px] px-2 py-0.5 rounded-full border border-dashed border-slate-300 text-slate-400">Pendiente</span>
                )}

                {/* Count input */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <input
                    type="number" min="0" inputMode="numeric"
                    value={p.counted_qty ?? ''}
                    onChange={(e) => onCountChange(p.product_id, e.target.value)}
                    className="w-16 h-10 text-center border-2 border-slate-300 rounded-md text-base font-bold focus:border-emerald-500 focus:outline-none"
                    data-testid={`inv-count-${p.product_id}`}
                    placeholder="—"
                  />
                  {isSaving && <span className="text-[9px] text-slate-400">…</span>}
                </div>

                {/* Info button (icon) */}
                <button
                  onClick={() => openInfo(p)}
                  className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-md bg-slate-100 hover:bg-sky-100 text-slate-600 hover:text-sky-700 text-base"
                  title="Información"
                  aria-label="Información"
                  data-testid={`inv-info-${p.product_id}`}
                >ℹ️</button>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <p className="text-center text-slate-400 py-8">Sin productos</p>
          )}
        </div>
      )}

      {/* Confirm reset modal */}
      {confirmReset && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-sm w-full p-5 space-y-3">
            <h3 className="text-lg font-bold text-rose-700">Reiniciar inventario</h3>
            <p className="text-sm text-slate-600">
              Se borrarán TODOS los conteos físicos capturados en la sucursal <b>{branchName}</b>. Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmReset(false)} className="px-3 py-1.5 rounded-lg bg-slate-200 text-sm">Cancelar</button>
              <button onClick={resetBranch} className="px-3 py-1.5 rounded-lg bg-rose-600 text-white text-sm font-semibold" data-testid="inv-reset-confirm">Sí, borrar</button>
            </div>
          </div>
        </div>
      )}

      {/* Pending finalize modal */}
      {pending && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-5 space-y-3 max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-rose-700">Faltan productos por contar</h3>
            <p className="text-sm text-slate-600">Debes capturar la cantidad (incluso <b>0</b>) de estos {pending.pending_count} productos antes de terminar:</p>
            <ul className="space-y-1 text-sm">
              {pending.pending.slice(0, 40).map(x => (
                <li key={x.product_id} className="border-b border-slate-100 py-1 text-slate-700">
                  <b>{x.name}</b> <span className="text-slate-400 text-xs">— {x.sku || 'sin SKU'}</span>
                </li>
              ))}
              {pending.pending_count > 40 && <li className="text-slate-400 text-xs">… y {pending.pending_count - 40} más</li>}
            </ul>
            <div className="flex justify-end">
              <button onClick={() => { setPending(null); setFilter('pending'); }} className="px-3 py-1.5 rounded-lg bg-slate-800 text-white text-sm">Ver pendientes</button>
            </div>
          </div>
        </div>
      )}

      {/* Summary modal */}
      {summary && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-4xl w-full p-5 space-y-3 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-slate-900">✅ Inventario finalizado — {summary.branch_name}</h3>
                <p className="text-xs text-slate-500">{new Date(summary.generated_at).toLocaleString('es-MX')}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={downloadPdf} className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-semibold" data-testid="inv-pdf-btn">📄 PDF (actual)</button>
                <button onClick={() => setSummary(null)} className="px-3 py-1.5 rounded-lg bg-slate-200 text-sm">Cerrar</button>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2 text-center text-xs">
              <div className="bg-slate-100 rounded p-2">Productos<br/><b className="text-lg">{summary.totals.productos}</b></div>
              <div className="bg-emerald-50 rounded p-2 text-emerald-700">Correctos<br/><b className="text-lg">{summary.totals.correctos}</b></div>
              <div className="bg-rose-50 rounded p-2 text-rose-700">Faltantes<br/><b className="text-lg">{summary.totals.con_faltantes}</b></div>
              <div className="bg-amber-50 rounded p-2 text-amber-700">Sobrantes<br/><b className="text-lg">{summary.totals.con_sobrantes}</b></div>
            </div>

            {/* Ajustes rápidos + guardar sesión */}
            <div className="border-2 border-dashed border-emerald-300 bg-emerald-50/50 rounded-xl p-3 space-y-2">
              <p className="font-bold text-emerald-800 text-sm">💾 Guardar inventario</p>
              <p className="text-xs text-slate-600">
                Se registrará como historial firmado. Opcionalmente puedes <b>aplicar las diferencias al stock</b> del sistema en un solo paso.
              </p>
              <label className="flex items-center gap-2 text-sm cursor-pointer bg-white rounded-lg border border-slate-200 p-2">
                <input
                  type="checkbox"
                  checked={applyAdjustments}
                  onChange={(e) => setApplyAdjustments(e.target.checked)}
                  className="w-4 h-4"
                  data-testid="inv-apply-adjustments"
                />
                <span>
                  Aplicar ajustes al stock <span className="text-slate-500">(faltantes/sobrantes se sumarán o restarán)</span>
                </span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase text-slate-500 font-semibold">Firma (nombre admin)</label>
                  <input
                    value={saveSignature}
                    onChange={(e) => setSaveSignature(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    placeholder="Ej. Roberto Admin"
                    data-testid="inv-signature"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase text-slate-500 font-semibold">
                    Motivo {applyAdjustments && <span className="text-rose-600">*</span>}
                  </label>
                  <input
                    value={saveReason}
                    onChange={(e) => setSaveReason(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    placeholder="Ej. Conteo mensual enero"
                    data-testid="inv-save-reason"
                  />
                </div>
              </div>
              <button
                onClick={saveSession}
                disabled={saving || (applyAdjustments && saveReason.trim().length < 5)}
                className="w-full py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white font-bold text-sm"
                data-testid="inv-save-session"
              >
                {saving ? 'Guardando…' : applyAdjustments ? '💾 Aplicar ajustes y guardar' : '💾 Guardar (sin ajustar stock)'}
              </button>
            </div>

            <table className="w-full text-xs">
              <thead className="bg-slate-100 text-slate-600 text-[10px] uppercase">
                <tr>
                  <th className="p-1 text-left">Producto</th>
                  <th className="p-1 text-right">Esperado</th>
                  <th className="p-1 text-right">Contado</th>
                  <th className="p-1 text-right">Balance</th>
                  <th className="p-1 text-right">Costo</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {summary.items.map(it => (
                  <tr key={it.product_id} className={
                    it.status === 'faltan' ? 'bg-rose-50' : it.status === 'sobran' ? 'bg-amber-50' : ''
                  }>
                    <td className="p-1">{it.name}</td>
                    <td className="p-1 text-right">{it.expected}</td>
                    <td className="p-1 text-right"><b>{it.counted}</b></td>
                    <td className={`p-1 text-right font-bold ${it.status === 'faltan' ? 'text-rose-700' : it.status === 'sobran' ? 'text-amber-700' : 'text-emerald-700'}`}>
                      {it.diff > 0 ? `+${it.diff}` : it.diff}
                    </td>
                    <td className="p-1 text-right text-slate-500">{it.purchase_cost != null ? `$${Number(it.purchase_cost).toFixed(2)}` : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Post-save confirmation */}
      {savedResult && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-5 space-y-3 text-center">
            <div className="text-4xl">✅</div>
            <h3 className="text-lg font-bold text-emerald-700">Inventario guardado</h3>
            <p className="text-sm text-slate-600">
              {savedResult.adjustments_applied
                ? <>Se aplicaron ajustes a <b>{savedResult.adjustments_summary?.applied || 0}</b> productos. Delta total: <b>{savedResult.adjustments_summary?.delta_total || 0}</b>.</>
                : <>Registro histórico creado. El stock del sistema <b>no</b> se modificó.</>}
            </p>
            <p className="text-[11px] text-slate-500">Sesión ID: <code>{savedResult.session_id}</code></p>
            <button
              onClick={() => setSavedResult(null)}
              className="w-full py-2 bg-slate-800 text-white rounded-lg text-sm"
              data-testid="inv-saved-close"
            >Continuar</button>
          </div>
        </div>
      )}

      {/* Product info modal — tabla unificada */}
      {infoModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setInfoModal(null)}>
          <div className="bg-white rounded-xl max-w-2xl w-full p-4 space-y-3 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div>
              <h3 className="text-lg font-bold text-slate-900">{infoModal.product.name}</h3>
              <p className="text-xs text-slate-500">Sistema: <b>{infoModal.product.system_stock}</b> · SKU: <b className="font-mono">{infoModal.product.sku || '-'}</b></p>
              <p className="text-[10px] text-slate-400 mt-1">Doble-clic en una fila para abrir el detalle.</p>
            </div>
            {infoModal.loading && <p className="text-slate-400 text-sm">Cargando…</p>}
            {!infoModal.loading && (() => {
              // Extraer sale_id de notas
              const extractSaleId = (notes) => {
                if (!notes) return null;
                const m = /Venta\s+([a-f0-9]{20,})/i.exec(notes);
                return m ? m[1] : null;
              };
              const extractShipFolio = (notes) => {
                if (!notes) return null;
                const m = /Envío\s+(E-\d+|ENV-[\w-]+)/.exec(notes);
                return m ? m[1] : null;
              };
              // Enriquecer movimientos
              const movs = (infoModal.movements || []).map(m => {
                const saleIdInNotes = extractSaleId(m.notes);
                const shipInNotes = extractShipFolio(m.notes);
                let label = labelForMov(m);
                let kind = MOV_TYPE_MAP[m.type]?.kind || null;
                let refId = m.reference_id;
                if (m.type === 'salida' && shipInNotes) { label = 'Envío'; kind = 'shipment'; }
                else if (m.type === 'salida' && saleIdInNotes) { label = 'Venta'; kind = 'sale'; }
                if (!refId && saleIdInNotes) refId = saleIdInNotes;
                return { ...m, _label: label, _kind: kind, _saleIdInNotes: saleIdInNotes, _shipFolio: shipInNotes, reference_id: refId };
              });
              // sale_ids que ya aparecen en movimientos (para no duplicar con recent_sales)
              const movSaleIds = new Set(movs.map(m => m._saleIdInNotes).filter(Boolean));
              // Ventas que NO están representadas en movimientos → añadirlas
              const salesExtra = (infoModal.sales || [])
                .filter(s => !movSaleIds.has(s.sale_id))
                .map(s => ({
                  id: `sale_${s.sale_id}`,
                  type: 'sale',
                  _label: 'Venta',
                  _kind: 'sale',
                  quantity: -Math.abs(s.quantity || 0),
                  created_at: s.created_at,
                  reference_id: s.sale_id,
                  notes: `Total $${Number(s.total || 0).toFixed(2)}`,
                }));
              const merged = [...movs, ...salesExtra];
              merged.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));

              if (merged.length === 0) {
                return <p className="text-center text-slate-400 text-sm py-6">Sin movimientos recientes</p>;
              }

              return (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 text-slate-600 text-[10px] uppercase sticky top-0">
                      <tr>
                        <th className="px-2 py-1.5 text-left">Fecha</th>
                        <th className="px-2 py-1.5 text-left">Tipo</th>
                        <th className="px-2 py-1.5 text-right">Cantidad</th>
                        <th className="px-2 py-1.5 text-left">Detalle</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {merged.map((m, i) => {
                        const clickable = !!m.reference_id;
                        return (
                          <tr
                            key={m.id || i}
                            onDoubleClick={() => clickable && openRefDetail(m.reference_id)}
                            className={`${clickable ? 'cursor-pointer hover:bg-sky-50' : 'cursor-default'}`}
                            title={clickable ? 'Doble-clic para ver detalle' : ''}
                            data-testid={`inv-mov-${i}`}
                          >
                            <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap">
                              {new Date(m.created_at).toLocaleString('es-MX', { year: '2-digit', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                            </td>
                            <td className="px-2 py-1.5">
                              <span className={`inline-block text-[10px] font-bold px-1.5 py-0.5 rounded ${
                                m._kind === 'sale' ? 'bg-sky-100 text-sky-700' :
                                m._kind === 'shipment' ? 'bg-orange-100 text-orange-700' :
                                m._kind === 'purchase' ? 'bg-emerald-100 text-emerald-700' :
                                m._kind === 'apartado' ? 'bg-indigo-100 text-indigo-700' :
                                m._kind === 'transfer' ? 'bg-teal-100 text-teal-700' :
                                m.type === 'entrada' ? 'bg-emerald-100 text-emerald-700' :
                                m.type === 'merma' ? 'bg-rose-100 text-rose-700' :
                                m.type?.includes('cancel') ? 'bg-slate-100 text-slate-600' :
                                m.type === 'physical_adjustment' ? 'bg-purple-100 text-purple-700' :
                                'bg-slate-100 text-slate-700'
                              }`}>
                                {m._label}
                              </span>
                            </td>
                            <td className={`px-2 py-1.5 text-right font-bold ${
                              m.quantity > 0 ? 'text-emerald-700' : m.quantity < 0 ? 'text-rose-700' : 'text-slate-500'
                            }`}>
                              {m.quantity > 0 ? '+' : ''}{m.quantity}
                            </td>
                            <td className="px-2 py-1.5 text-slate-500 truncate max-w-[220px]" title={m.notes || ''}>
                              {m.notes || (clickable ? '(ver detalle…)' : '-')}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()}
            <button onClick={() => setInfoModal(null)} className="w-full py-2 bg-slate-800 text-white rounded-lg text-sm">Cerrar</button>
          </div>
        </div>
      )}

      {/* Ref detail modal (venta/envío/compra/apartado/transferencia) */}
      {refDetail && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={() => setRefDetail(null)}>
          <div className="bg-white rounded-xl max-w-lg w-full p-4 space-y-3 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {refDetail.loading && <p className="text-slate-500 text-sm py-4">Cargando…</p>}
            {!refDetail.loading && refDetail.error && (
              <p className="text-rose-700 text-sm py-2">{refDetail.error}</p>
            )}
            {!refDetail.loading && !refDetail.error && refDetail.kind && (() => {
              const kindLabel = {
                sale: '🧾 Venta',
                shipment: '🚚 Envío',
                purchase: '🛒 Compra',
                apartado: '📦 Apartado',
                transfer: '🔀 Transferencia',
              }[refDetail.kind] || refDetail.kind;
              return (
                <>
                  <div>
                    <p className="text-[10px] uppercase font-bold text-slate-400">{kindLabel}</p>
                    <h3 className="text-lg font-bold text-slate-900">
                      {refDetail.folio || refDetail.id}
                      {refDetail.status && (
                        <span className={`ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          refDetail.status === 'cancelled' ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'
                        }`}>{refDetail.status}</span>
                      )}
                    </h3>
                    <p className="text-xs text-slate-500">
                      {refDetail.created_at && new Date(refDetail.created_at).toLocaleString('es-MX')}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs bg-slate-50 rounded-lg p-2">
                    {refDetail.customer_name && (
                      <div><b>Cliente:</b><br/>{refDetail.customer_name}</div>
                    )}
                    {refDetail.customer_phone && (
                      <div><b>Tel:</b><br/>{refDetail.customer_phone}</div>
                    )}
                    {refDetail.supplier_name && (
                      <div><b>Proveedor:</b><br/>{refDetail.supplier_name}</div>
                    )}
                    {refDetail.deliverer_name && (
                      <div><b>Entregador:</b><br/>{refDetail.deliverer_name}</div>
                    )}
                    {refDetail.seller_name && (
                      <div><b>Vendedor:</b><br/>{refDetail.seller_name}</div>
                    )}
                    {refDetail.address && (
                      <div className="col-span-2"><b>Dirección:</b><br/>{refDetail.address}</div>
                    )}
                    {refDetail.payment_method && (
                      <div><b>Pago:</b><br/>{refDetail.payment_method}</div>
                    )}
                    {refDetail.total != null && (
                      <div><b>Total:</b><br/>${Number(refDetail.total).toFixed(2)}</div>
                    )}
                    {refDetail.paid_total != null && (
                      <div><b>Abonado:</b><br/>${Number(refDetail.paid_total).toFixed(2)}</div>
                    )}
                    {refDetail.shipping_cost != null && (
                      <div><b>Envío:</b><br/>${Number(refDetail.shipping_cost).toFixed(2)}</div>
                    )}
                    {refDetail.total_qty != null && (
                      <div><b>Piezas:</b><br/>{refDetail.total_qty}</div>
                    )}
                    {refDetail.cancellation_reason && (
                      <div className="col-span-2"><b>Motivo cancelación:</b><br/>{refDetail.cancellation_reason}</div>
                    )}
                    {refDetail.notes && (
                      <div className="col-span-2"><b>Notas:</b><br/>{refDetail.notes}</div>
                    )}
                  </div>

                  {(refDetail.items || []).length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase font-bold text-slate-500 mb-1">Productos</p>
                      <table className="w-full text-xs">
                        <tbody className="divide-y">
                          {refDetail.items.map((it, i) => (
                            <tr key={i}>
                              <td className="py-1"><b>{it.quantity}×</b> {it.product_name}</td>
                              {it.unit_price != null && (
                                <td className="py-1 text-right text-slate-500">${Number(it.unit_price).toFixed(2)}</td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              );
            })()}
            <button onClick={() => setRefDetail(null)} className="w-full py-2 bg-slate-800 text-white rounded-lg text-sm">Cerrar</button>
          </div>
        </div>
      )}

      {/* Image zoom modal */}
      {imgModal && (
        <div
          className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setImgModal(null)}
          data-testid="inv-image-zoom"
        >
          <img src={imgModal.url} alt={imgModal.name} className="max-h-[90vh] max-w-full rounded-lg shadow-2xl" />
        </div>
      )}
    </div>
  );
}
